/**
 * @module plugins/export-excel
 * @desc Registers ExportExcelButton and v-exportable directive globally, and auto-injects
 *       the export button into card headers that contain tables.
 */
import Vue from 'vue';
import ExportExcelButton from '@/components/ExportExcelButton.vue';

Vue.component('ExportExcelButton', ExportExcelButton);

// Mark a container as the scope for auto table lookup
Vue.directive('exportable', {
  inserted(el) {
    el.setAttribute('data-export-scope', '');
  }
});

/**
 * Auto-inject the Export button into any .card that has a .card-header and contains one or more tables.
 * - Opt-out: add `data-no-auto-export` on the card or the header.
 * - Multiple tables in the same card become separate sheets via the `selectors` prop.
 * - Prevent duplicates by marking injected headers with a data attribute.
 */
function injectExportButtons(rootEl, vmCtx) {
  if (!rootEl || !rootEl.querySelectorAll) return;
  const cards = rootEl.querySelectorAll('.card');

  let autoIdCounter = 1;

  // 1) Handle cards with headers: place button in .card-header
  if (cards && cards.length) {
    cards.forEach(card => {
      if (card.hasAttribute('data-no-auto-export')) return;
      const header = card.querySelector('.card-header');
      if (!header || header.hasAttribute('data-no-auto-export')) return;

      // Avoid duplicate injection
      if (header.querySelector('[data-auto-export-injected]')) return;

      // Find tables within the card
      const tables = card.querySelectorAll('table, .vue-good-table table, .vgt-table');
      if (!tables || !tables.length) return;

      // Assign IDs to all tables (if missing) and collect selectors
      const selectors = [];
      tables.forEach(tbl => {
        if (!tbl.id) {
          // generate a unique id per page
          tbl.id = `auto_export_table_${Date.now()}_${autoIdCounter++}`;
        }
        selectors.push(`#${tbl.id}`);
      });

      // Derive a filename from the card header text or fallback to route path
      const titleText = (header.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60);
      const routePath = (vmCtx && vmCtx.$route && vmCtx.$route.path)
        ? vmCtx.$route.path.replace(/[\/#+]+/g, '-').replace(/^-+|-+$/g, '')
        : 'export';
      const baseName = titleText || routePath || 'export';

      // Find or create a shared actions container inside header so PDF & Excel sit on one row
      let actions = header.querySelector('[data-export-actions]');
      if (!actions) {
        actions = document.createElement('div');
        actions.setAttribute('data-export-actions', '');
        actions.className = 'export-actions ms-auto';
        // Ensure header aligns content correctly
        const styles = window.getComputedStyle(header);
        if (styles.display !== 'flex') header.classList.add('d-flex', 'align-items-center');
        header.appendChild(actions);
      }

      // Mount the Vue component programmatically and append to shared container
      // Guard: if an Excel button already exists in this actions container, skip
      const alreadyHasExcel = actions.querySelector('[data-role="excel-button"]');
      if (!alreadyHasExcel) {
        const BtnCtor = Vue.extend(ExportExcelButton);
        const vm = new BtnCtor({ propsData: { selectors, filename: baseName, label: 'Excel' } });
        vm.$mount();
        vm.$el.setAttribute('data-role', 'excel-button');
        actions.appendChild(vm.$el);
      }

      // Mark header as processed to avoid duplicate injections on subsequent updates
      if (!header.querySelector('[data-auto-export-injected]')) {
        const marker = document.createElement('span');
        marker.setAttribute('data-auto-export-injected', '');
        marker.style.display = 'none';
        header.appendChild(marker);
      }
    });
  }

  // 2) Handle standalone tables not inside a .card: inject a small toolbar before the table
  const standaloneTables = Array.from(rootEl.querySelectorAll('table, .vue-good-table table, .vgt-table'))
    .filter(tbl => !tbl.closest('.card'));

  standaloneTables.forEach(tbl => {
    // Robust de‑duplication: if we've already handled this specific table, skip
    if (tbl.hasAttribute('data-auto-export-injected')) return;

    if (!tbl.id) {
      tbl.id = `auto_export_table_${Date.now()}_${autoIdCounter++}`;
    }
    const selectors = [`#${tbl.id}`];

    // Reuse or create a shared toolbar before the table. Look for any existing toolbar
    // in the same parent (not only the immediate previous sibling) to avoid duplicates
    // when the table re-renders (e.g., on store switch).
    const parent = tbl.parentNode;
    let bar = null;
    if (parent && parent.querySelector) {
      bar = parent.querySelector(':scope > [data-export-toolbar]');
    }
    if (!bar) {
      bar = document.createElement('div');
      bar.setAttribute('data-export-toolbar', '');
      bar.className = 'export-actions d-flex justify-content-end mb-2';
      // Insert toolbar before the table
      parent && parent.insertBefore(bar, tbl);
    }

    // Guard: only one Excel button per toolbar
    const alreadyHasExcel = bar.querySelector('[data-role="excel-button"]');
    if (!alreadyHasExcel) {
      const BtnCtor = Vue.extend(ExportExcelButton);
      const routePath = (vmCtx && vmCtx.$route && vmCtx.$route.path)
        ? vmCtx.$route.path.replace(/[\/#]+/g, '-').replace(/^-+|-+$/g, '')
        : 'export';
      const vm = new BtnCtor({ propsData: { selectors, filename: routePath, label: 'Excel' } });
      vm.$mount();
      vm.$el.setAttribute('data-role', 'excel-button');
      bar.appendChild(vm.$el);
    }

    // Mark the table as processed
    tbl.setAttribute('data-auto-export-injected', '');
  });
}

// Use a global mixin to run injection after component mount/update
Vue.mixin({
  mounted() {
    // Defer to next tick to ensure DOM is rendered
    this.$nextTick(() => injectExportButtons(this.$el || document.body, this));
  },
  updated() {
    // Re-run in case tables are loaded asynchronously
    this.$nextTick(() => injectExportButtons(this.$el || document.body, this));
  }
});
